import React, { useEffect, useRef, useState } from "react";
import { Award, Sparkles } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { TEAM } from "@/content";

const ACCENT = {
  green: {
    border: "border-green-100",
    chip: "bg-green-50 text-green-700 border-green-100",
    badge: "bg-green-600",
    soft: "bg-green-50",
    bgFrom: "from-green-100",
    bgTo: "to-emerald-200",
    ring: "shadow-green-200/60",
  },
  emerald: {
    border: "border-emerald-100",
    chip: "bg-emerald-50 text-emerald-800 border-emerald-100",
    badge: "bg-emerald-700",
    soft: "bg-emerald-50",
    bgFrom: "from-emerald-100",
    bgTo: "to-teal-200",
    ring: "shadow-emerald-200/60",
  },
  rose: {
    border: "border-rose-100",
    chip: "bg-rose-50 text-rose-700 border-rose-100",
    badge: "bg-rose-600",
    soft: "bg-rose-50",
    bgFrom: "from-rose-100",
    bgTo: "to-pink-200",
    ring: "shadow-rose-200/60",
  },
};

// CSS personalizado: flotación continua + bobblehead al hover
const FUNKO_STYLES = `
  @keyframes funko-float {
    0%, 100% { transform: translateY(0) rotate(0deg); }
    50% { transform: translateY(-8px) rotate(-1deg); }
  }
  @keyframes funko-float-alt {
    0%, 100% { transform: translateY(0) rotate(0deg); }
    50% { transform: translateY(-10px) rotate(1.5deg); }
  }
  @keyframes funko-float-slow {
    0%, 100% { transform: translateY(0) rotate(0deg); }
    50% { transform: translateY(-6px) rotate(-1.5deg); }
  }
  .funko-float-0 { animation: funko-float 4s ease-in-out infinite; }
  .funko-float-1 { animation: funko-float-alt 5s ease-in-out infinite; animation-delay: .5s; }
  .funko-float-2 { animation: funko-float-slow 4.5s ease-in-out infinite; animation-delay: 1s; }

  /* Bobble (sacudida tipo cabeza Funko) al hacer hover sobre la tarjeta */
  @keyframes funko-bobble {
    0%, 100% { transform: rotate(0deg); }
    20% { transform: rotate(-7deg); }
    40% { transform: rotate(6deg); }
    60% { transform: rotate(-4deg); }
    80% { transform: rotate(3deg); }
  }
  .funko-card:hover .funko-avatar {
    animation: funko-bobble 0.9s ease-in-out;
  }

  /* Halo orbital sutil detrás del avatar */
  @keyframes funko-halo {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  .funko-halo {
    animation: funko-halo 18s linear infinite;
  }
`;

export const Team = () => {
  const { lang } = useLang();
  const tEs = lang === "es";

  // Parallax con el scroll (offset suave) para que los avatares se muevan
  const [scrollY, setScrollY] = useState(0);
  const sectionRef = useRef(null);

  useEffect(() => {
    const onScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      // Centrado respecto al viewport (negativo cuando section pasa hacia arriba)
      const center = rect.top + rect.height / 2 - window.innerHeight / 2;
      setScrollY(center);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <section
      ref={sectionRef}
      id="team"
      className="relative py-24 md:py-32 bg-white overflow-hidden"
      data-testid="team-section"
    >
      <style>{FUNKO_STYLES}</style>

      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-semibold text-green-700">
            <Award className="h-3.5 w-3.5" /> {tEs ? "Nuestro equipo" : "Our team"}
          </span>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">
            {tEs ? "Profesionales que cuidan de ti" : "Professionals who care for you"}
          </h2>
          <p className="mt-4 text-lg text-slate-600 leading-relaxed">
            {tEs
              ? "Un equipo multidisciplinario formado en las mejores escuelas, con certificaciones internacionales y la calidez humana que tu recuperación necesita."
              : "A multidisciplinary team trained in top schools, with international certifications and the warmth your recovery deserves."}
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {TEAM.map((p, i) => {
            const a = ACCENT[p.accent];
            // Parallax: cada tarjeta se desplaza ligeramente distinto
            const parallaxOffset = (scrollY * (0.04 + i * 0.015)) * -1; // px
            return (
              <article
                key={p.id}
                className={`funko-card group rounded-3xl border ${a.border} bg-white shadow-sm hover:shadow-2xl transition-all duration-500 overflow-hidden flex flex-col hover:-translate-y-1`}
                data-testid={`team-card-${p.id}`}
              >
                {/* Zona del avatar Funko */}
                <div
                  className={`relative aspect-[4/5] overflow-hidden bg-gradient-to-br ${a.bgFrom} ${a.bgTo}`}
                >
                  {/* Halo orbital decorativo */}
                  <div
                    className="funko-halo absolute inset-0 pointer-events-none opacity-60"
                    style={{
                      background:
                        "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.55) 0%, transparent 35%), radial-gradient(circle at 70% 70%, rgba(255,255,255,0.4) 0%, transparent 30%)",
                    }}
                  />
                  {/* Suelo / sombra */}
                  <div className="absolute bottom-6 left-1/2 -translate-x-1/2 w-44 h-3 rounded-full bg-black/15 blur-md" />

                  {/* Avatar con float + parallax */}
                  <div
                    className={`funko-avatar absolute inset-0 flex items-end justify-center funko-float-${i % 3}`}
                    style={{
                      transform: `translateY(${parallaxOffset}px)`,
                      transition: "transform 0.2s ease-out",
                      willChange: "transform",
                    }}
                  >
                    <img
                      src={p.photo}
                      alt={`Avatar Funko de ${p.name}, ${p.role}`}
                      loading="lazy"
                      className={`h-full w-full object-contain drop-shadow-2xl ${a.ring}`}
                      data-testid={`team-avatar-${p.id}`}
                    />
                  </div>

                  <div
                    className={`absolute bottom-3 left-3 inline-flex items-center gap-1.5 rounded-full ${a.badge} text-white px-3 py-1 text-[10px] font-bold uppercase tracking-widest shadow-md z-10`}
                  >
                    <Sparkles className="h-3 w-3" />
                    {p.yearsExperience}+ {tEs ? "años" : "years"}
                  </div>
                </div>

                <div className={`p-6 flex-1 ${a.soft}`}>
                  <h3 className="font-display text-xl font-bold tracking-tight text-slate-900">
                    {p.name}
                  </h3>
                  <p className="mt-1 text-sm font-medium text-slate-700">{p.role}</p>
                  {p.license && (
                    <p className="mt-1 text-[11px] text-slate-500">{p.license}</p>
                  )}

                  <p className="mt-4 text-sm leading-relaxed text-slate-600">
                    {tEs ? p.bio_es : p.bio_en}
                  </p>

                  <div className="mt-5">
                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                      {tEs ? "Especialidades" : "Specialties"}
                    </p>
                    <ul className="mt-2 flex flex-wrap gap-1.5">
                      {(tEs ? p.specialties_es : p.specialties_en).map((sp) => (
                        <li
                          key={sp}
                          className={`inline-flex rounded-full ${a.chip} border px-2.5 py-1 text-[11px] font-medium`}
                        >
                          {sp}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Team;
